// Service 15 — งานทะเบียนราษฎรและเอกสารสถานะบุคคลสำหรับใช้ในประเทศและต่างประเทศ
// อ้างอิง: พระราชบัญญัติการทะเบียนราษฎร พ.ศ. 2534 (แก้ไขเพิ่มเติม พ.ศ. 2551),
// พระราชบัญญัติชื่อบุคคล พ.ศ. 2505, พระราชบัญญัติบัตรประจำตัวประชาชน พ.ศ. 2526,
// พระราชบัญญัติสัญชาติ พ.ศ. 2508, ประมวลกฎหมายแพ่งและพาณิชย์ บรรพ 5,
// สำนักบริหารการทะเบียน กรมการปกครอง (bora.dopa.go.th) และกรมการกงสุล (consular.mfa.go.th)
// Category definitions only — client-safe, no heavy FAQ payload.

export type CivilRegistryFaqCategory =
  | "birth-registration"
  | "death-registration"
  | "house-registration"
  | "name-change"
  | "thai-id-card"
  | "family-status-records"
  | "child-legitimation-adoption"
  | "born-abroad-nationality"
  | "registry-translation-legalization"
  | "corrections-copies-replacements";

export interface CivilRegistryCategoryDef {
  slug: CivilRegistryFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const CIVIL_REGISTRY_FAQ_CATEGORIES: CivilRegistryCategoryDef[] = [
  {
    slug: "birth-registration",
    titleTh: "แจ้งเกิดและสูติบัตร",
    titleEn: "Birth Registration and Birth Certificates",
    descTh:
      "กำหนดเวลาแจ้งเกิดตามพระราชบัญญัติการทะเบียนราษฎร พ.ศ. 2534 ผู้มีหน้าที่แจ้ง เอกสารที่สำนักทะเบียนเรียก การแจ้งเกิดเกินกำหนด และการขอสูติบัตรฉบับภาษาอังกฤษเพื่อใช้ต่างประเทศ",
    descEn:
      "Deadlines for reporting a birth under the Civil Registration Act B.E. 2534, who must report, documents the district office asks for, late birth registration, and obtaining the English-format birth record for overseas use.",
    matchTh: /แจ้งเกิด|สูติบัตร|เด็กเกิด|ท\.ร\.1|แจ้งเกิดเกินกำหนด|หนังสือรับรองการเกิด/,
    matchEn: /birth registration|birth certificate|newborn|late birth|record of birth/i,
  },
  {
    slug: "death-registration",
    titleTh: "แจ้งตายและมรณบัตร",
    titleEn: "Death Registration and Death Certificates",
    descTh:
      "การแจ้งตายภายในกำหนด ผู้มีหน้าที่แจ้ง หนังสือรับรองการตายจากสถานพยาบาล การจำหน่ายชื่อออกจากทะเบียนบ้าน และการใช้มรณบัตรในคดีมรดกและการเคลมประกันในต่างประเทศ",
    descEn:
      "Reporting a death within the statutory deadline, who must report, the hospital certificate of death, removal of the name from the house register, and using the death certificate for probate and overseas insurance claims.",
    matchTh: /แจ้งตาย|มรณบัตร|ผู้ตาย|เสียชีวิต|จำหน่ายชื่อ|ท\.ร\.4/,
    matchEn: /death registration|death certificate|deceased|removal from the house register/i,
  },
  {
    slug: "house-registration",
    titleTh: "ทะเบียนบ้าน ย้ายเข้า–ย้ายออก และทะเบียนบ้านชาวต่างชาติ",
    titleEn: "House Registration, Moves, and the Yellow Book for Foreigners",
    descTh:
      "ทะเบียนบ้าน (ท.ร.14) การย้ายเข้าย้ายออก ทะเบียนบ้านกลาง และทะเบียนบ้านสำหรับคนต่างชาติ (ท.ร.13 หรือ “เล่มเหลือง”) รวมถึงการนำทะเบียนบ้านไปใช้กับธนาคาร กรมที่ดิน และงานวีซ่า",
    descEn:
      "The Thai house register (Tabien Baan, T.R.14), moving in and out, the central register, and the yellow book (T.R.13) for foreign residents — plus how banks, the Land Department and immigration use it.",
    matchTh: /ทะเบียนบ้าน|ย้ายเข้า|ย้ายออก|เล่มเหลือง|ท\.ร\.13|ท\.ร\.14|ทะเบียนบ้านกลาง|เจ้าบ้าน/,
    matchEn: /house register|Tabien Baan|yellow book|T\.R\.13|T\.R\.14|house-?master|move in|move out/i,
  },
  {
    slug: "name-change",
    titleTh: "เปลี่ยนชื่อตัว ชื่อรอง และชื่อสกุล",
    titleEn: "Changing a First Name, Middle Name or Surname",
    descTh:
      "การเปลี่ยนชื่อตัวและชื่อสกุลตามพระราชบัญญัติชื่อบุคคล พ.ศ. 2505 การใช้ชื่อสกุลคู่สมรส การกลับไปใช้ชื่อสกุลเดิมหลังหย่า และผลกระทบต่อหนังสือเดินทาง วุฒิการศึกษา และเอกสารต่างประเทศ",
    descEn:
      "Changing a given name or surname under the Person Name Act B.E. 2505, adopting a spouse's surname, reverting after divorce, and the knock-on effects on passports, diplomas and foreign documents.",
    matchTh: /เปลี่ยนชื่อ|ชื่อสกุล|นามสกุล|ช\.3|ช\.5|ชื่อรอง|กลับไปใช้ชื่อสกุลเดิม/,
    matchEn: /change (of )?name|surname|maiden name|middle name|name change certificate/i,
  },
  {
    slug: "thai-id-card",
    titleTh: "บัตรประจำตัวประชาชนและเลข 13 หลัก",
    titleEn: "Thai ID Card and the 13-Digit Number",
    descTh:
      "การทำบัตรประจำตัวประชาชนครั้งแรก บัตรหาย บัตรหมดอายุ การขอบัตรจากต่างประเทศ และการใช้เลขประจำตัวประชาชน 13 หลักในเอกสารทางกฎหมายและการยืนยันตัวตน",
    descEn:
      "First-time Thai ID cards, lost or expired cards, applying while living abroad, and how the 13-digit personal identification number is used in legal documents and identity verification.",
    matchTh: /บัตรประจำตัวประชาชน|บัตรประชาชน|เลข 13 หลัก|เลขประจำตัวประชาชน|บัตรหาย/,
    matchEn: /ID card|identification card|13-digit|national ID|lost card/i,
  },
  {
    slug: "family-status-records",
    titleTh: "ทะเบียนสมรส ทะเบียนหย่า และหนังสือรับรองสถานภาพ",
    titleEn: "Marriage, Divorce and Status Certificates",
    descTh:
      "ใบสำคัญการสมรส (คร.3) ทะเบียนสมรส (คร.2) ใบสำคัญการหย่า หนังสือรับรองความเป็นโสด และหนังสือรับรองสถานะบุคคลที่สถานทูตต่างประเทศเรียกก่อนจดทะเบียน",
    descEn:
      "The marriage certificate (Kor Ror 3), marriage register (Kor Ror 2), divorce certificate, single-status affidavits, and the family-status letters foreign embassies request before registration.",
    matchTh: /ทะเบียนสมรส|ใบสำคัญการสมรส|คร\.2|คร\.3|ทะเบียนหย่า|ความเป็นโสด|สถานภาพสมรส|รับรองสถานะ/,
    matchEn: /marriage certificate|Kor Ror|divorce certificate|single status|family status/i,
  },
  {
    slug: "child-legitimation-adoption",
    titleTh: "รับรองบุตร อำนาจปกครอง และรับบุตรบุญธรรม",
    titleEn: "Legitimation, Custody and Adoption Records",
    descTh:
      "การจดทะเบียนรับรองบุตร (คร.11) อำนาจปกครองบุตรของบิดามารดาที่ไม่ได้จดทะเบียนสมรส บันทึกข้อตกลงหลังหย่า และทะเบียนรับบุตรบุญธรรมตามกฎหมายไทย",
    descEn:
      "Registering legitimation of a child (Kor Ror 11), parental power where the parents never married, post-divorce custody agreements, and adoption registration under Thai law.",
    matchTh: /รับรองบุตร|คร\.11|อำนาจปกครอง|บุตรบุญธรรม|บิดาโดยชอบด้วยกฎหมาย|ปกครองบุตร/,
    matchEn: /legitimation|parental power|custody|adoption/i,
  },
  {
    slug: "born-abroad-nationality",
    titleTh: "เกิดนอกประเทศ สัญชาติไทย และทะเบียนราษฎรที่สถานทูต",
    titleEn: "Born Abroad, Thai Nationality and Registration at Embassies",
    descTh:
      "การแจ้งเกิดบุตรที่เกิดต่างประเทศต่อสถานเอกอัครราชทูตหรือสถานกงสุลใหญ่ การได้สัญชาติไทยโดยการเกิด การเพิ่มชื่อเข้าทะเบียนบ้าน และการถือสองสัญชาติของเด็ก",
    descEn:
      "Reporting a birth abroad to a Thai embassy or consulate-general, acquiring Thai nationality by birth, adding the child to a Thai house register, and dual nationality for children.",
    matchTh: /เกิดต่างประเทศ|สถานเอกอัครราชทูต|สถานกงสุล|สัญชาติไทย|สองสัญชาติ|เพิ่มชื่อเข้าทะเบียนบ้าน/,
    matchEn: /born abroad|embassy|consulate|Thai nationality|dual nationality|add(ed)? to the house register/i,
  },
  {
    slug: "registry-translation-legalization",
    titleTh: "แปลและรับรองนิติกรณ์เอกสารทะเบียนราษฎร",
    titleEn: "Translating and Legalising Civil Registry Documents",
    descTh:
      "การคัดสำเนาแบบรับรองจากสำนักทะเบียน การแปลเป็นภาษาอังกฤษ การรับรองคำแปลที่กองสัญชาติและนิติกรณ์ กรมการกงสุล การรับรองต่อที่สถานทูตปลายทาง และ Apostille ซึ่งจะมีผลกับไทยวันที่ 28 กุมภาพันธ์ 2570",
    descEn:
      "Certified copies from the district office, English translation, legalisation by the Legalization Division of the Department of Consular Affairs, further attestation at the destination embassy, and the Apostille — effective for Thailand on 28 February 2027.",
    matchTh: /แปล|คำแปล|นิติกรณ์|กรมการกงสุล|Apostille|อพอสติล|คัดสำเนา|รับรองเอกสาร/,
    matchEn: /translat|legalis|legaliz|consular|apostille|certified copy/i,
  },
  {
    slug: "corrections-copies-replacements",
    titleTh: "แก้ไขข้อมูลผิด ขอใบแทน และคัดรับรองสำเนา",
    titleEn: "Corrections, Replacements and Certified Copies",
    descTh:
      "การแก้ไขวันเดือนปีเกิด ชื่อสะกดผิด หรือข้อมูลคลาดเคลื่อนในทะเบียนราษฎร การขอใบแทนสูติบัตร/มรณบัตรที่สูญหาย และการคัดรับรองรายการทะเบียนราษฎรจากฐานข้อมูลกลาง",
    descEn:
      "Correcting a date of birth, misspelt name or inconsistent entry in the civil registration database, replacing a lost birth or death certificate, and obtaining certified extracts from the central registry.",
    matchTh: /แก้ไข|สะกดผิด|ใบแทน|สูญหาย|คัดรับรองรายการ|ข้อมูลไม่ตรง|ฐานข้อมูลทะเบียน/,
    matchEn: /correct|misspel|replacement|lost|certified extract|mismatch/i,
  },
];

export function getCivilRegistryCategoryDef(slug: string): CivilRegistryCategoryDef | undefined {
  return CIVIL_REGISTRY_FAQ_CATEGORIES.find((c) => c.slug === slug);
}